import React, { useEffect, useState } from "react";
import { ErrorPlaceholder } from "../../../components/common/ErrorPlaceholder";
import { LoadingPlaceholder } from "../../../components/common/LoadingPlaceholder";
import { Button } from "../../../components/button";
import { Badge } from "../../../components/badge";
import { showToast } from "../../../components/toast-store";
import { Check } from "lucide-react";
import _ from "lodash";
import { BillingPlan } from "./MerchantPlan.api";
import { MERCHANT_PLAN_NOTIFICATION_MESSAGES } from "./MerchantPlan.const";
import { useMerchantPlanData } from "./hooks/useMerchantPlanData";
import { useUpgradePlan } from "./hooks/useUpgradePlan";

const MerchantPlanPage: React.FC = () => {
  const [upgradingPlan, setUpgradingPlan] = useState<"basic" | "premium" | null>(null);
  const { entitlementProfile, billingPlans, loading, error } = useMerchantPlanData();
  const { upgrade } = useUpgradePlan();

  useEffect(() => {
    if (error && !entitlementProfile) {
      showToast({
        type: "error",
        title: "Error",
        message: MERCHANT_PLAN_NOTIFICATION_MESSAGES.FETCH_ERROR,
      });
    }
  }, [entitlementProfile, error]);

  const handleUpgrade = async (planType: "basic" | "premium") => {
    if (upgradingPlan) {
      return;
    }

    setUpgradingPlan(planType);
    try {
      await upgrade(planType);
      showToast({
        type: "success",
        title: "Success",
        message: "Checkout opened. Complete payment to activate your plan.",
      });
    } catch (upgradeError) {
      showToast({
        type: "error",
        title: "Error",
        message:
          upgradeError instanceof Error
            ? upgradeError.message
            : MERCHANT_PLAN_NOTIFICATION_MESSAGES.UPGRADE_ERROR,
      });
    } finally {
      setUpgradingPlan(null);
    }
  };

  const isUpgrading = upgradingPlan !== null;

  const normalizePlanCode = (value: string | null | undefined) =>
    (value || "").trim().toLowerCase();

  const findPlan = (planCode: "basic" | "premium"): BillingPlan | null =>
    billingPlans.find((plan) => normalizePlanCode(plan.code) === planCode) ||
    null;

  const basicPlan = findPlan("basic");
  const premiumPlan = findPlan("premium");

  const currentPlanName = entitlementProfile?.subscription.plan_name || "";
  const normalizedCurrentPlan = normalizePlanCode(currentPlanName);

  if (loading) {
    return <LoadingPlaceholder />;
  }

  if (error && _.isNil(entitlementProfile?.subscription)) {
    return <ErrorPlaceholder error={error} />;
  }

  const currentFeatures = (plan_name: string) => {
    let result = [];
    if (normalizePlanCode(plan_name) === "basic") {
      const maxActivieCoupon = entitlementProfile?.quota_usage.find(
        (quota) => quota.feature_key === "max_active_coupons",
      );
      if (maxActivieCoupon) {
        result.push(`Up to ${maxActivieCoupon.limit} active coupons`);
      }
      result.push(`Verified Partner badge`);
      result.push(
        "Standard redemption controls (quantity up to 10,000, low stock alerts, limit 1–10 per user)",
      );
      result.push("Unlimited events/surveys per month");
      result.push(
        "Advanced analytics (attendance rates, demographics, trends, CSV export)",
      );
      result.push("Email support with 24-48 hour response time");
      result.push("Branded egift card market place included");
      return result;
    }

    if (normalizePlanCode(plan_name) === "premium") {
      const maxActivieCoupon = entitlementProfile?.quota_usage.find(
        (quota) => quota.feature_key === "max_active_coupons",
      );
      if (maxActivieCoupon) {
        result.push(`Up to ${maxActivieCoupon.limit} active coupons`);
      }
      result.push(`Premium Partner badge`);
      result.push(
        "Advanced redemption controls (unlimited quantity, time-based availability, minimum purchase requirements)",
      );
      result.push("Unlimited events/surveys per month");
      result.push(
        "Advanced analytics (attendance rates, demographics, trends, CSV export)",
      );
      result.push("Email or phone support (12 hour response)");
      result.push("Branded egift card market place included");
      return result;
    }

    result = [];
    result.push(`Up to ${1} active coupons`);
    result.push("Standard listing (alphabetical order)");
    result.push(
      "Basic redemption controls (points, quantity up to 100, expiry, limit 1–3 per user)",
    );
    result.push("2 events/surveys per month");
    result.push("Views analytics only");
    result.push("Email support (3–5 business days)");
    return result;
  };
  return (
    <div className="bg-white dark:bg-gray-900/50 min-h-screen p-6">
      <div className="max-w-full">
        {/* Header */}
        <h1
          className="text-2xl font-bold text-black dark:text-white leading-8 mb-10"
          style={{ fontFamily: "Nunito" }}
        >
          My Plan
        </h1>

        {/* Current Plan Section — Free / Basic / Premium */}
        <div className="bg-gradient-to-br from-green-50 to-sky-100 dark:from-emerald-950/20 dark:to-emerald-900/20 outline outline-1 outline-offset-[-1px] outline-teal-500/20 rounded-xl p-5 mb-6">
          <div className="flex flex-col gap-5">
            <div className="h-12 border-b-[0.50px] border-zinc-300 dark:border-gray-600 flex justify-between items-center">
              <h2
                className="text-base font-semibold text-black dark:text-white leading-6"
                style={{ fontFamily: "Nunito" }}
              >
                Current Plan
              </h2>
              <Badge
                className="bg-teal-500 dark:bg-emerald-500 text-white px-2 pt-[2.40px] pb-[3px] rounded text-[10px] font-bold border-0"
                style={{ fontFamily: "Nunito" }}
              >
                {entitlementProfile?.subscription.plan_name}
              </Badge>
            </div>
            <ul className="flex flex-col gap-1.5">
              {currentFeatures(
                entitlementProfile?.subscription.plan_name ?? "free",
              ).map((feature, index) => (
                <li key={index} className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-teal-500 dark:text-emerald-400 flex-shrink-0" />
                  <span
                    className="text-sm font-normal text-black dark:text-gray-300 leading-5"
                    style={{ fontFamily: "Nunito" }}
                  >
                    {feature}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Upgrade Options Section — hidden when Premium */}
        {normalizedCurrentPlan !== "premium" && (
          <div className="mb-6">
            <h2
              className="text-2xl font-semibold text-slate-950 dark:text-white leading-6 mb-6"
              style={{ fontFamily: "Nunito" }}
            >
              Upgrade options
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Basic Plan Card */}
              <div className="flex-1 p-5 rounded-xl outline outline-1 outline-offset-[-1px] outline-teal-500/20 bg-white dark:bg-gray-800 flex flex-col gap-5">
                <div className="flex flex-col gap-5">
                  <div className="h-12 border-b-[0.50px] border-zinc-300 dark:border-gray-600 flex justify-between items-center">
                    <h3
                      className="text-base font-semibold text-black dark:text-white leading-6"
                      style={{ fontFamily: "Nunito" }}
                    >
                      Basic Plan
                    </h3>
                    <Badge
                      className="bg-teal-500 dark:bg-emerald-500 text-white px-2 pt-[2.40px] pb-[3px] rounded text-xs font-bold border-0"
                      style={{ fontFamily: "Nunito" }}
                    >
                      {basicPlan?.monthlyPrice !== null &&
                      basicPlan?.monthlyPrice !== undefined
                        ? `$${basicPlan.monthlyPrice}/mo`
                        : "Contact us"}
                    </Badge>
                  </div>
                  <ul className="flex flex-col gap-1.5 flex-grow">
                    {currentFeatures("basic").map((feature, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <Check className="h-4 w-4 text-teal-500 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
                        <span
                          className="text-sm font-normal text-black dark:text-gray-300 leading-5"
                          style={{ fontFamily: "Nunito" }}
                        >
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="flex flex-col">
                  {basicPlan?.monthlyPrice !== null &&
                    basicPlan?.monthlyPrice !== undefined &&
                    basicPlan?.annualPrice !== null &&
                    basicPlan?.annualPrice !== undefined && (
                      <p
                        className="text-black/80 dark:text-gray-300 text-sm font-bold leading-5 mb-4"
                        style={{ fontFamily: "Nunito" }}
                      >
                        Save $
                        {basicPlan.monthlyPrice * 12 - basicPlan.annualPrice}
                        /year with annual billing
                      </p>
                    )}
                </div>
                <Button
                  onClick={() => handleUpgrade("basic")}
                  disabled={normalizedCurrentPlan === "basic" || isUpgrading}
                  className="w-full bg-emerald-500 dark:bg-emerald-500 hover:bg-emerald-600 dark:hover:bg-emerald-600 text-white px-6 py-3 rounded-lg text-sm font-extrabold leading-5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ fontFamily: "Nunito" }}
                >
                  {normalizedCurrentPlan === "basic"
                    ? "Current plan"
                    : upgradingPlan === "basic"
                      ? "Processing..."
                      : "Upgrade to Basic plan"}
                </Button>
              </div>

              {/* Premium Plan Card */}
              <div className="flex-1 p-5 rounded-xl outline outline-1 outline-offset-[-1px] outline-teal-500/20 bg-white dark:bg-gray-800 flex flex-col gap-5">
                <div className="flex flex-col gap-5">
                  <div className="h-12 border-b-[0.50px] border-zinc-300 dark:border-gray-600 flex justify-between items-center">
                    <h3
                      className="text-base font-semibold text-black dark:text-white leading-6"
                      style={{ fontFamily: "Nunito" }}
                    >
                      Premium Plan
                    </h3>
                    <Badge
                      className="bg-teal-500 dark:bg-emerald-500 text-white px-2 pt-[2.40px] pb-[3px] rounded text-xs font-bold border-0"
                      style={{ fontFamily: "Nunito" }}
                    >
                      {premiumPlan?.monthlyPrice !== null &&
                      premiumPlan?.monthlyPrice !== undefined
                        ? `$${premiumPlan.monthlyPrice}/mo`
                        : "Contact us"}
                    </Badge>
                  </div>
                  <ul className="flex flex-col gap-1.5 flex-grow">
                    {currentFeatures("premium").map((feature, index) => (
                      <li key={index} className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-teal-500 dark:text-emerald-400 flex-shrink-0" />
                        <span
                          className="text-sm font-normal text-black dark:text-gray-300 leading-5"
                          style={{ fontFamily: "Nunito" }}
                        >
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="flex flex-col">
                  {premiumPlan?.monthlyPrice !== null &&
                    premiumPlan?.monthlyPrice !== undefined &&
                    premiumPlan?.annualPrice !== null &&
                    premiumPlan?.annualPrice !== undefined && (
                      <p
                        className="text-black/80 dark:text-gray-300 text-sm font-bold leading-5 mb-4"
                        style={{ fontFamily: "Nunito" }}
                      >
                        Save $
                        {premiumPlan.monthlyPrice * 12 -
                          premiumPlan.annualPrice}
                        /year with annual billing
                      </p>
                    )}
                </div>
                <Button
                  onClick={() => handleUpgrade("premium")}
                  disabled={isUpgrading}
                  className="w-full bg-emerald-500 dark:bg-emerald-500 hover:bg-emerald-600 dark:hover:bg-emerald-600 text-white px-6 py-3 rounded-lg text-sm font-extrabold leading-5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ fontFamily: "Nunito" }}
                >
                  {upgradingPlan === "premium"
                    ? "Processing..."
                    : "Upgrade to Premium plan"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MerchantPlanPage;
